import { useState, useEffect } from 'react';
import { LuBuilding2, LuInfo } from 'react-icons/lu';
import { FiHome, FiPlus, FiMapPin, FiEdit2, FiTrash2, FiChevronRight, FiX } from 'react-icons/fi';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

export default function PropertySetup() {
  const { t } = useTranslation();
  const [properties, setProperties] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [showPropertyModal, setShowPropertyModal] = useState(false);
  const [showBuildingModal, setShowBuildingModal] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);

  const [editingProperty, setEditingProperty] = useState(null);
  const [editingBuilding, setEditingBuilding] = useState(null);

  const [propertyForm, setPropertyForm] = useState({
    name: '',
    address: '',
    description: ''
  });

  const [buildingForm, setBuildingForm] = useState({
    name: '',
    code: '',
    property_id: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [propsRes, bldgsRes] = await Promise.all([
      window.ezsite.apis.tablePage(70646, { PageNo: 1, PageSize: 100, OrderByField: 'name', IsAsc: true }),
      window.ezsite.apis.tablePage(70647, { PageNo: 1, PageSize: 500, OrderByField: 'name', IsAsc: true })]
      );

      const props = propsRes?.data?.List || [];
      const bldgs = bldgsRes?.data?.List || [];

      setProperties(props);
      setBuildings(bldgs);

      if (props.length > 0 && !selectedProperty) {
        setSelectedProperty(props[0]);
      } else if (selectedProperty) {
        // Refresh selected property data
        const updated = props.find((p) => p.id === selectedProperty.id);
        if (updated) setSelectedProperty(updated);
      }
    } catch (error) {
      toast.error(t('propertySetup.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handlePropertySubmit = async (e) => {
    e.preventDefault();
    if (!propertyForm.name || !propertyForm.address) {
      toast.error(t('propertySetup.nameAddressRequired'));
      return;
    }

    setSubmitting(true);
    try {
      if (editingProperty) {
        const { error } = await window.ezsite.apis.tableUpdate(70646, {
          id: editingProperty.id,
          ...propertyForm
        });
        if (error) throw error;
        toast.success(t('propertySetup.propertyUpdated'));
      } else {
        const { error } = await window.ezsite.apis.tableCreate(70646, propertyForm);
        if (error) throw error;
        toast.success(t('propertySetup.propertyCreated'));
      }

      setShowPropertyModal(false);
      setEditingProperty(null);
      setPropertyForm({ name: '', address: '', description: '' });
      await loadData();
    } catch (error) {
      toast.error(editingProperty ? t('propertySetup.propertyUpdated') : t('propertySetup.propertyCreated'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleBuildingSubmit = async (e) => {
    e.preventDefault();
    if (!buildingForm.name || !buildingForm.code) {
      toast.error(t('propertySetup.nameCodeRequired'));
      return;
    }

    setSubmitting(true);
    try {
      if (editingBuilding) {
        const { error } = await window.ezsite.apis.tableUpdate(70647, {
          id: editingBuilding.id,
          ...buildingForm,
          property_id: selectedProperty.id
        });
        if (error) throw error;
        toast.success(t('propertySetup.buildingUpdated'));
      } else {
        const { error } = await window.ezsite.apis.tableCreate(70647, {
          ...buildingForm,
          property_id: selectedProperty.id
        });
        if (error) throw error;
        toast.success(t('propertySetup.buildingAdded'));
      }

      setShowBuildingModal(false);
      setEditingBuilding(null);
      setBuildingForm({ name: '', code: '', property_id: '' });
      await loadData();
    } catch (error) {
      toast.error(editingBuilding ? t('propertySetup.buildingUpdated') : t('propertySetup.buildingAdded'));
    } finally {
      setSubmitting(false);
    }
  };

  const startEditProperty = (prop) => {
    setEditingProperty(prop);
    setPropertyForm({
      name: prop.name,
      address: prop.address,
      description: prop.description || ''
    });
    setShowPropertyModal(true);
  };

  const startEditBuilding = (bldg) => {
    setEditingBuilding(bldg);
    setBuildingForm({
      name: bldg.name,
      code: bldg.code,
      property_id: bldg.property_id
    });
    setShowBuildingModal(true);
  };

  const handleDeleteProperty = async (id) => {
    if (!confirm(t('propertySetup.deletePropertyConfirm'))) return;
    try {
      await window.ezsite.apis.tableDelete(70646, { ID: id });
      toast.success(t('propertySetup.propertyDeleted'));
      if (selectedProperty?.id === id) setSelectedProperty(null);
      loadData();
    } catch (error) {
      toast.error(t('propertySetup.deleteFailed'));
    }
  };

  const handleDeleteBuilding = async (id) => {
    if (!confirm(t('propertySetup.deleteBuildingConfirm'))) return;
    try {
      await window.ezsite.apis.tableDelete(70647, { ID: id });
      toast.success(t('propertySetup.buildingDeleted'));
      loadData();
    } catch (error) {
      toast.error(t('propertySetup.deleteFailed'));
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
        <p className="text-gray-500 animate-pulse">{t('propertySetup.loading')}</p>
      </div>);

  }

  return (
    <div className="max-w-7xl mx-auto space-y-8 animate-in fade-in duration-500">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-6 rounded-2xl border border-gray-200 shadow-sm">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight">{t('propertySetup.title')}</h1>
          <p className="text-gray-600 mt-1">{t('propertySetup.subtitle')}</p>
        </div>
        <button
          onClick={() => {
            setEditingProperty(null);
            setPropertyForm({ name: '', address: '', description: '' });
            setShowPropertyModal(true);
          }}
          className="flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all shadow-lg shadow-blue-200 active:scale-95">

          <FiHome className="w-5 h-5" />
          <span className="font-semibold">{t('propertySetup.newProperty')}</span>
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Properties List - Left Pane */}
        <div className="lg:col-span-4 space-y-4">
          <div className="flex items-center justify-between px-2">
            <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
              <FiMapPin className="w-5 h-5 text-blue-600" /> 
              {t('propertySetup.properties')}
              <span className="bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded-full">{properties.length}</span>
            </h2>
          </div>
          
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden divide-y divide-gray-100">
            {properties.length === 0 ?
            <div className="p-8 text-center">
                <FiHome className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 text-sm">{t('propertySetup.noProperties')}</p>
              </div> :

            properties.map((prop) =>
            <div
              key={prop.id}
              className={`group p-4 cursor-pointer transition-all relative ${
              selectedProperty?.id === prop.id ? 'bg-blue-50 border-l-4 border-l-blue-600' : 'hover:bg-gray-50 border-l-4 border-l-transparent'}`
              }
              onClick={() => setSelectedProperty(prop)}>

                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <h3 className={`font-bold truncate ${selectedProperty?.id === prop.id ? 'text-blue-700' : 'text-gray-900'}`}>
                        {prop.name}
                      </h3>
                      <p className="text-xs text-gray-500 truncate mt-0.5">{prop.address}</p>
                    </div>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                    onClick={(e) => {e.stopPropagation();startEditProperty(prop);}}
                    className="p-1.5 text-gray-400 hover:text-blue-600 rounded-lg hover:bg-white transition">

                        <FiEdit2 className="w-4 h-4" />
                      </button>
                      <button
                    onClick={(e) => {e.stopPropagation();handleDeleteProperty(prop.id);}}
                    className="p-1.5 text-gray-400 hover:text-red-600 rounded-lg hover:bg-white transition">

                        <FiTrash2 className="w-4 h-4" />
                      </button>
                      <FiChevronRight className={`w-5 h-5 ${selectedProperty?.id === prop.id ? 'text-blue-400' : 'text-gray-300'}`} />
                    </div>
                  </div>
                </div>
            )
            }
          </div>
        </div>

        {/* Buildings Management - Right Pane */}
        <div className="lg:col-span-8 space-y-4">
          {!selectedProperty ?
          <div className="bg-white rounded-2xl border border-gray-200 border-dashed p-12 flex flex-col items-center justify-center text-center h-full">
              <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mb-4">
                <LuBuilding2 className="w-8 h-8 text-gray-300" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">{t('propertySetup.noPropertySelected')}</h3>
              <p className="text-gray-500 max-w-xs mt-2">{t('propertySetup.selectPropertyDesc')}</p>
            </div> :

          <>
              <div className="flex items-center justify-between px-2">
                <div>
                  <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                    <LuBuilding2 className="w-6 h-6 text-purple-600" />
                    {t('propertySetup.buildingsIn', { name: selectedProperty.name })}
                  </h2>
                  <p className="text-sm text-gray-500 mt-0.5">{selectedProperty.address}</p>
                </div>
                <button
                onClick={() => {
                  setEditingBuilding(null);
                  setBuildingForm({ name: '', code: '', property_id: selectedProperty.id });
                  setShowBuildingModal(true);
                }}
                className="flex items-center gap-2 px-4 py-2 bg-purple-50 text-purple-700 rounded-xl hover:bg-purple-100 transition font-semibold text-sm border border-purple-100">

                  <FiPlus className="w-4 h-4" /> {t('propertySetup.addBuilding')}
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {buildings.filter((b) => b.property_id === selectedProperty.id).length === 0 ?
              <div className="col-span-full bg-white rounded-2xl border border-gray-200 p-12 text-center">
                    <p className="text-gray-500">{t('propertySetup.noBuildings')}</p>
                    <button
                  onClick={() => setShowBuildingModal(true)}
                  className="mt-4 text-blue-600 font-medium hover:underline text-sm">

                      {t('propertySetup.clickToAddBuilding')}
                    </button>
                  </div> :

              buildings.filter((b) => b.property_id === selectedProperty.id).map((b) =>
              <div key={b.id} className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition group">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-purple-50 rounded-xl flex items-center justify-center text-purple-600">
                            <LuBuilding2 className="w-5 h-5" />
                          </div>
                          <div>
                            <h3 className="font-bold text-gray-900">{b.name}</h3>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-[10px] font-bold uppercase tracking-wider bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">
                                Code: {b.code}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <button
                      onClick={() => startEditBuilding(b)}
                      className="p-2 text-gray-400 hover:text-blue-600 rounded-lg hover:bg-gray-50 transition">

                            <FiEdit2 className="w-4 h-4" />
                          </button>
                          <button
                      onClick={() => handleDeleteBuilding(b.id)}
                      className="p-2 text-gray-400 hover:text-red-600 rounded-lg hover:bg-gray-50 transition">

                            <FiTrash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
              )
              }
              </div>

              {/* Property Details Info Card */}
              <div className="bg-blue-50 rounded-2xl p-6 border border-blue-100 flex gap-4 mt-8">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <LuInfo className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-bold text-blue-900">{t('propertySetup.propertyOverview')}</h4>
                  <p className="text-sm text-blue-800/80 mt-1">
                    {selectedProperty.description || t('propertySetup.noDescription')}
                  </p>
                </div>
              </div>
            </>
          }
        </div>
      </div>

      {/* Property Modal */}
      {showPropertyModal &&
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-in fade-in zoom-in duration-200">
          <div className="bg-white rounded-3xl max-w-md w-full p-8 shadow-2xl relative">
            <button
            onClick={() => setShowPropertyModal(false)}
            className="absolute top-6 right-6 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition">

              <FiX className="w-5 h-5" />
            </button>
            
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900">{editingProperty ? t('propertySetup.editProperty') : t('propertySetup.addNewProperty')}</h2>
              <p className="text-gray-500 text-sm mt-1">{editingProperty ? t('propertySetup.updatePropertyDesc') : t('propertySetup.createPropertyDesc')}</p>
            </div>

            <form onSubmit={handlePropertySubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1.5">{t('propertySetup.propertyName')}</label>
                <input
                required
                className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all outline-none"
                placeholder="e.g. Skyline Residency"
                value={propertyForm.name}
                onChange={(e) => setPropertyForm({ ...propertyForm, name: e.target.value })} />

              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1.5">{t('propertySetup.fullAddress')}</label>
                <input
                required
                className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all outline-none"
                placeholder="Street, City, Postcode"
                value={propertyForm.address}
                onChange={(e) => setPropertyForm({ ...propertyForm, address: e.target.value })} />

              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1.5">{t('propertySetup.descriptionOptional')}</label>
                <textarea
                className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all outline-none resize-none"
                rows={3}
                placeholder="Brief details about the property..."
                value={propertyForm.description}
                onChange={(e) => setPropertyForm({ ...propertyForm, description: e.target.value })} />

              </div>
              <div className="flex gap-3 pt-4">
                <button
                type="button"
                onClick={() => setShowPropertyModal(false)}
                className="flex-1 px-4 py-3 border border-gray-200 rounded-xl font-bold text-gray-600 hover:bg-gray-50 transition">

                  {t('common.cancel')}
                </button>
                <button
                type="submit"
                disabled={submitting}
                className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition disabled:opacity-50">

                  {submitting ? t('propertySetup.saving') : editingProperty ? t('propertySetup.updateProperty') : t('propertySetup.createProperty')}
                </button>
              </div>
            </form>
          </div>
        </div>
      }

      {/* Building Modal */}
      {showBuildingModal &&
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-in fade-in zoom-in duration-200">
          <div className="bg-white rounded-3xl max-w-md w-full p-8 shadow-2xl relative">
            <button
            onClick={() => setShowBuildingModal(false)}
            className="absolute top-6 right-6 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition">

              <FiX className="w-5 h-5" />
            </button>

            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900">{editingBuilding ? t('propertySetup.editBuilding') : t('propertySetup.addBuilding')}</h2>
              <p className="text-gray-500 text-sm mt-1">{t('propertySetup.registerBuildingUnder')} <span className="text-blue-600 font-bold">{selectedProperty?.name}</span></p>
            </div>

            <form onSubmit={handleBuildingSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1.5">{t('propertySetup.buildingNameBlock')}</label>
                <input
                required
                className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-purple-500 focus:bg-white transition-all outline-none"
                placeholder="e.g. Block A or North Wing"
                value={buildingForm.name}
                onChange={(e) => setBuildingForm({ ...buildingForm, name: e.target.value })} />

              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1.5">{t('propertySetup.buildingCode')}</label>
                <input
                required
                className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-purple-500 focus:bg-white transition-all outline-none font-mono"
                placeholder="e.g. SR-BLK-A"
                value={buildingForm.code}
                onChange={(e) => setBuildingForm({ ...buildingForm, code: e.target.value })} />

                <p className="text-[10px] text-gray-500 mt-1 px-1 italic">{t('propertySetup.buildingCodeDesc')}</p>
              </div>
              <div className="flex gap-3 pt-6">
                <button
                type="button"
                onClick={() => setShowBuildingModal(false)}
                className="flex-1 px-4 py-3 border border-gray-200 rounded-xl font-bold text-gray-600 hover:bg-gray-50 transition">

                  {t('common.cancel')}
                </button>
                <button
                type="submit"
                disabled={submitting}
                className="flex-1 px-4 py-3 bg-purple-600 text-white rounded-xl font-bold hover:bg-purple-700 transition disabled:opacity-50 shadow-lg shadow-purple-100">

                  {submitting ? t('propertySetup.saving') : editingBuilding ? t('propertySetup.updateBuilding') : t('propertySetup.addBuilding')}
                </button>
              </div>
            </form>
          </div>
        </div>
      }
    </div>);

}